import { apiSlice } from './apiSlice'

export type ReportsDashboard = {
  total_cases: number
  total_teams: number
  total_courtrooms: number
  submitted_scores_count: number
  average_total_score: number | null
  leaderboard: Array<{
    team_id: string
    team_name: string
    avg_score: number
    score_count: number
  }>
}

export type RoundCompletion = {
  case_id: string
  round_id: string
  expected_total: number
  submitted_count: number
  complete: boolean
  judges: Array<{
    judge_id: string
    done: number
    expected: number
    complete: boolean
  }>
  teams: string[]
}

export type RankingRow = {
  rank: number
  team_id: string
  team_name: string
  judge_count: number
  average_score: number
  total_score: number
}

export type RankingResult = {
  case_id: string
  round_id: string | null
  total_teams: number
  ranking: RankingRow[]
}

export type PublishRoundBody = {
  case_id: string
  round_id: string
  advancing_team_ids: string[]
  title?: string
  note?: string
}

export type PublishRoundResult = {
  published_id: string
  case_id: string
  round_id: string
  advancing_team_ids: string[]
  notified_teams: number
  ranking: RankingRow[]
}

export const reportsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getReportsDashboard: builder.query<ReportsDashboard, void>({
      query: () => '/reports/dashboard',
    }),
    exportScoresCsv: builder.query<Blob, void>({
      query: () => ({
        url: '/reports/export/scores.csv',
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    exportSummaryTxt: builder.query<string, void>({
      query: () => ({
        url: '/reports/export/summary.txt',
        responseHandler: (response: Response) => response.text(),
      }),
    }),
    exportSummaryPdf: builder.query<Blob, void>({
      query: () => ({
        url: '/reports/export/summary.pdf',
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    // UAT-TC #15: kiểm tra giám khảo đã chấm xong
    getRoundCompletion: builder.query<RoundCompletion, { case_id: string; round_id: string }>({
      query: ({ case_id, round_id }) => ({
        url: '/reports/round-completion',
        params: { case_id, round_id },
      }),
    }),
    // UAT-TC #15 + Chung kết #16: ranking theo vòng (round_id=null → tổng hợp)
    getRanking: builder.query<RankingResult, { case_id: string; round_id?: string | null }>({
      query: ({ case_id, round_id }) => ({
        url: '/reports/ranking',
        params: round_id ? { case_id, round_id } : { case_id },
      }),
    }),
    // UAT-TC #16 + Bán kết #11 + Chung kết #18: công bố kết quả
    publishRound: builder.mutation<PublishRoundResult, PublishRoundBody>({
      query: (body) => ({
        url: '/reports/publish-round',
        method: 'POST',
        body,
      }),
    }),
  }),
})

export const {
  useGetReportsDashboardQuery,
  useLazyExportScoresCsvQuery,
  useLazyExportSummaryTxtQuery,
  useLazyExportSummaryPdfQuery,
  useGetRoundCompletionQuery,
  useLazyGetRoundCompletionQuery,
  useGetRankingQuery,
  useLazyGetRankingQuery,
  usePublishRoundMutation,
} = reportsApi
